import React from "react";
import { alpha, Alert, Box, Button, CircularProgress, Divider, Paper, Typography, useTheme } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";

interface Props {
  onGenerate: () => void;
  isGenerating: boolean;
  isMaxJobsReached: boolean;
  generateDisabled: boolean;
  viewedCount: number;
  totalVideos: number;
}

export const GenerateReportActions: React.FC<Props> = ({
  onGenerate,
  isGenerating,
  isMaxJobsReached,
  generateDisabled,
  viewedCount,
  totalVideos,
}) => {
  const theme = useTheme();

  return (
    <Box>
      {generateDisabled && (
        <Alert
          severity="warning"
          icon={<VideoLibraryIcon />}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            "& .MuiAlert-message": { fontSize: "0.85rem" },
          }}
        >
          <Typography variant="body2">
            Please review all {totalVideos} videos before generating ({viewedCount}/{totalVideos} viewed)
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: theme.palette.primary.main,
            color: "white",
            fontWeight: 700,
            fontSize: "1rem",
            flexShrink: 0,
          }}
        >
          3
        </Box>
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.2 }}
        >
          Generate Report
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Button
        variant="contained"
        onClick={onGenerate}
        color="primary"
        size="large"
        fullWidth
        disabled={isGenerating || isMaxJobsReached || generateDisabled}
        startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <BoltIcon />}
        sx={{
          py: 1.75,
          fontWeight: 700,
          borderRadius: 2.5,
          fontSize: "1rem",
          textTransform: "none",
          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
            transform: "translateY(-2px)",
          },
          "&:active": { transform: "translateY(0)" },
          "&:disabled": {
            background: theme.palette.grey[300],
            color: theme.palette.grey[500],
            boxShadow: "none",
          },
        }}
      >
        {isGenerating
          ? "Generating..."
          : isMaxJobsReached
            ? "Maximum Jobs Reached"
            : generateDisabled
              ? `Review Videos (${viewedCount}/${totalVideos})`
              : "Generate"}
      </Button>

      {isMaxJobsReached && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.warning.dark, fontWeight: 500, fontSize: "0.8rem" }}>
            Please wait for existing jobs to complete
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
